import { IList, ISelectableOption, List, mergeStyles } from '@fluentui/react';
import { ComboBox, IComboBoxProps, Loader, LoaderType } from '@h2oai/ui-kit';
import React from 'react';

const classNameLoaderItem = mergeStyles({
  paddingBlock: `0.5rem`,
});

type LoaderItemProps = {
  onFetchMoreItems: () => void;
};

const LoaderItem = (props: LoaderItemProps) => {
  React.useEffect(() => {
    props.onFetchMoreItems();
  }, []);
  return (
    <div className={classNameLoaderItem}>
      <Loader type={LoaderType.spinner} />
    </div>
  );
};

const classNameNoItemsFound = mergeStyles({
  paddingInline: `1rem`,
  fontStyle: `italic`,
});

const FETCH_MORE_KEY = '__fetch_more__';
const NO_ITEMS_FOUND_KEY = '__no_items_found__';

const FETCH_MORE_ITEM: ISelectableOption = {
  key: FETCH_MORE_KEY,
  text: ``,
};

export type ComboBoxInfiniteScrollProps = Omit<IComboBoxProps, `onRenderList`> & {
  optionsFiltered?: boolean; // If you limit the options with inputValue (e.g. by server-side filtering), set this to true
  onFetchMoreItems?: () => void;
};

export const ComboBoxInfiniteScroll = (props: ComboBoxInfiniteScrollProps) => {
  const { onFetchMoreItems, options } = props;
  const optionsWithFetchMore = onFetchMoreItems ? [...options, FETCH_MORE_ITEM] : options;
  const listRef = React.useRef<IList>(null);
  const inputValueRef = React.useRef<string>(``);
  return (
    <ComboBox
      {...props}
      onInputValueChange={(value) => {
        inputValueRef.current = value;
        props.onInputValueChange?.(value);
      }}
      options={
        props.optionsFiltered && optionsWithFetchMore.length === 0 && inputValueRef.current
          ? // if options are empty because of inputValue triggered filtering, show "no items found"
            [{ key: NO_ITEMS_FOUND_KEY, text: inputValueRef.current }]
          : optionsWithFetchMore
      }
      onRenderList={(comboBoxProps) => {
        if (!comboBoxProps) return null;
        const { id, onRenderItem } = comboBoxProps;
        return (
          <List
            componentRef={listRef}
            role="listbox"
            id={`${id}-list`}
            aria-labelledby={`${id}-label`}
            items={comboBoxProps.options}
            /* The List component takes the viewport height (at minimum) as a area to place items and multiplies it by
               this `renderedWindows...` value and starts rendering virtual _pages_ to populate that space. We set
              `renderedWindows...` to zero which only renders pages to fit on viewport height. Since the callout of the
               ComboBox is limited to be smaller than viewport, there is no risk of rendering too few items and we
               optimize pre-fetching, because with e.g. renderedWindowsAhead={2} (the default value), the List component
               would render pages to fit 3x viewport height, which might be like 20x callout height.
               I looked for a way to make the List component count with callout instead of viewport, but it is not
               supported and I did not find any hack around it.
               @see https://github.com/microsoft/fluentui/blob/8e2d0bb11cdd24dd76df04306c5b63b74739d8a5/packages/react/src/components/List/List.tsx#L1187
            */
            renderedWindowsAhead={0}
            renderedWindowsBehind={0}
            onRenderCell={
              !onRenderItem
                ? () => null
                : (item?: ISelectableOption) => {
                    if (!item) return null;
                    switch (item.key) {
                      case FETCH_MORE_KEY:
                        // Non-null assertion is safe because `FETCH_MORE_KEY` is appended only if `onFetchMoreItems` is provided.
                        return <LoaderItem key={item.index} onFetchMoreItems={onFetchMoreItems!} />;
                      case NO_ITEMS_FOUND_KEY:
                        return <div className={classNameNoItemsFound}>No items found</div>;
                      default:
                        return onRenderItem(item);
                    }
                  }
            }
          />
        );
      }}
      onScrollToItem={(itemIndex: number): void => {
        listRef.current && listRef.current.scrollToIndex(itemIndex);
      }}
    />
  );
};
